import { useEffect, useMemo, useState } from "react";
import { FileText, Plus, Wallet } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { openStatementPdf } from "@/features/invoices/api";
import { buildBillTo } from "@/features/invoices/lib/bill-to";
import { useFirmSettings } from "@/features/settings/hooks/use-firm-settings";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { InvoiceStatusBadge } from "@/features/invoices/components/invoice-status-badge";
import { RecordPaymentDialog } from "@/features/invoices/components/record-payment-dialog";
import { getClientInvoiceTotals, getInvoiceLedgerForClient } from "@/features/invoices/lib/invoice-selectors";
import { useInvoiceStore } from "@/features/invoices/store/invoice-store";
import type { Payment } from "@/features/invoices/types";
import { useActiveClient } from "@/layouts/client-layout";
import { cn, formatCents } from "@/lib/utils";

export default function ClientInvoicesListPage() {
  const { client } = useActiveClient();
  const navigate = useNavigate();
  const invoices = useInvoiceStore((s) => s.invoices);
  const lineItems = useInvoiceStore((s) => s.lineItems);
  const payments = useInvoiceStore((s) => s.payments);
  const invoicePayments = useInvoiceStore((s) => s.invoicePayments);
  const createDraftInvoice = useInvoiceStore((s) => s.createDraftInvoice);
  const loadClient = useInvoiceStore((s) => s.loadClient);
  const { settings } = useFirmSettings();

  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState<Payment | undefined>(undefined);

  useEffect(() => {
    loadClient(client.id);
  }, [client.id, loadClient]);

  const ledgerRows = useMemo(
    () => getInvoiceLedgerForClient(client.id, { invoices, lineItems, payments, invoicePayments }),
    [client.id, invoices, lineItems, payments, invoicePayments],
  );
  const totals = useMemo(
    () => getClientInvoiceTotals(client.id, { invoices, lineItems, payments, invoicePayments }),
    [client.id, invoices, lineItems, payments, invoicePayments],
  );

  async function handleNewInvoice() {
    const id = await createDraftInvoice(client.id, {
      billTo: buildBillTo(client),
      // Defaults from firm settings (fall back to no-tax / due-on-receipt).
      taxPercent: settings ? Number(settings.invoiceDefaultTaxPercent) || 0 : 0,
      terms: settings?.invoiceDefaultTerms ?? "RECEIPT",
    });
    navigate(id);
  }

  async function handleStatement() {
    // Pre-open a tab synchronously so the popup blocker doesn't kill it.
    const tab = window.open("", "_blank");
    try {
      await openStatementPdf(client.id, tab);
    } catch {
      tab?.close();
      toast.error("Couldn't generate the statement.");
    }
  }

  function openNewPayment() {
    setEditingPayment(undefined);
    setPaymentDialogOpen(true);
  }

  function openEditPayment(payment: Payment) {
    setEditingPayment(payment);
    setPaymentDialogOpen(true);
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <div>
          <h1 className="text-foreground mb-2 text-3xl font-bold">Invoices</h1>
          <p className="text-muted-foreground max-w-2xl">Invoices and payments for this client.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleStatement}>
            <FileText /> Statement
          </Button>
          <Button variant="outline" size="sm" onClick={openNewPayment}>
            <Wallet /> Payment
          </Button>
          <Button size="sm" onClick={handleNewInvoice}>
            <Plus /> New Invoice
          </Button>
        </div>
      </div>

      <Card className="gap-0 overflow-hidden p-0">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/40">
              <TableHead>Type</TableHead>
              <TableHead>Invoice/Ref #</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Aging</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead className="text-right">Applied</TableHead>
              <TableHead className="text-right">Balance</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {ledgerRows.map((row) =>
              row.kind === "invoice" ? (
                <TableRow
                  key={row.invoice.id}
                  role="button"
                  onClick={() => navigate(row.invoice.id)}
                  className="cursor-pointer"
                >
                  <TableCell>Invoice</TableCell>
                  <TableCell className="font-medium">{row.invoice.invoiceNumber}</TableCell>
                  <TableCell>{row.invoice.invoiceDate}</TableCell>
                  <TableCell>
                    {row.invoice.status === "OPEN" && row.invoice.agingDays > 0 ? `${row.invoice.agingDays}d` : ""}
                  </TableCell>
                  <TableCell>
                    <InvoiceStatusBadge status={row.invoice.status} />
                  </TableCell>
                  <TableCell
                    className={cn("text-right", row.invoice.status === "VOID" && "text-muted-foreground line-through")}
                  >
                    {formatCents(row.invoice.totalCents)}
                  </TableCell>
                  <TableCell
                    className={cn("text-right", row.invoice.status === "VOID" && "text-muted-foreground line-through")}
                  >
                    {formatCents(row.invoice.appliedCents)}
                  </TableCell>
                  <TableCell className="text-right font-semibold">
                    {row.invoice.status === "DRAFT"
                      ? `(${formatCents(row.invoice.balanceCents)})`
                      : formatCents(row.invoice.balanceCents)}
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow
                  key={row.payment.id}
                  role="button"
                  onClick={() => openEditPayment(row.payment)}
                  className="cursor-pointer"
                >
                  <TableCell>Payment</TableCell>
                  <TableCell className="font-medium">{row.payment.reference || "—"}</TableCell>
                  <TableCell>{row.payment.paymentDate}</TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell className="text-right">{formatCents(-row.payment.paymentAmtCents)}</TableCell>
                  <TableCell className="text-right">{formatCents(-row.payment.appliedCents)}</TableCell>
                  <TableCell className="text-right">
                    {row.payment.unappliedCents > 0 ? (
                      <span className="text-warning">Unapplied: {formatCents(row.payment.unappliedCents)}</span>
                    ) : (
                      formatCents(-row.payment.unappliedCents)
                    )}
                  </TableCell>
                </TableRow>
              ),
            )}
            {ledgerRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-muted-foreground text-center">
                  No invoices or payments yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={7}>Total Due</TableCell>
              <TableCell className="text-right font-bold">{formatCents(totals.balanceCents)}</TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </Card>

      <RecordPaymentDialog
        clientId={client.id}
        open={paymentDialogOpen}
        onOpenChange={setPaymentDialogOpen}
        editingPayment={editingPayment}
      />
    </div>
  );
}
